import React from 'react';
import Layout from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { AlertTriangle } from 'lucide-react';

const LegalDisclaimerPage = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Legal Disclaimer",
    "description": "Legal disclaimer for Redspire Civil Engineering services and website content.",
    "url": "https://redspire.com/legal-disclaimer",
    "publisher": {
      "@type": "Organization",
      "name": "Redspire Civil Engineering"
    }
  };

  return (
    <Layout>
      <SEOHead
        title="Legal Disclaimer | Redspire Civil Engineering"
        description="Legal disclaimer and limitations of liability for Redspire Civil Engineering services, website content, and professional engineering information."
        keywords={['legal disclaimer', 'liability limitations', 'professional disclaimer']}
        url="https://redspire.com/legal-disclaimer"
        schema={schema}
      />

      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <AlertTriangle className="w-12 h-12 text-yellow-500" />
            <h1 className="text-4xl md:text-5xl font-bold">Legal Disclaimer</h1>
          </div>
          <p className="text-xl text-gray-300">Important information about our services and website</p>
          <p className="text-sm text-gray-400 mt-4">Effective Date: January 1, 2025</p>
        </div>
      </div>

      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-12">
            <p className="text-gray-800 font-semibold mb-2">Important Notice</p>
            <p className="text-gray-700">
              This Legal Disclaimer applies to all content, services, and information provided by Redspire Civil Engineering. Please read this disclaimer carefully before using our services or relying on any information provided on this website.
            </p>
          </div>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">1. No Professional Relationship</h2>
            <p className="text-gray-700 leading-relaxed">
              The content on this website is for general informational purposes only and does not create a professional engineer-client relationship. Viewing this website, downloading materials, or contacting Redspire does not establish a client relationship or obligate Redspire to provide services.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Professional engineering services require a written agreement that defines scope, terms, and conditions. Do not rely on website content as a substitute for professional engineering consultation specific to your project.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">2. No Engineering Advice</h2>
            <p className="text-gray-700 leading-relaxed">
              Information provided on this website, including project descriptions, technical articles, and general engineering content, is for educational and marketing purposes only. This information does not constitute professional engineering advice, design recommendations, or specifications for your specific project or site.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4 font-semibold">
              Do not make engineering, construction, or project decisions based solely on website content. Always consult with a licensed professional engineer for project-specific guidance.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">3. Accuracy of Information</h2>
            <p className="text-gray-700 leading-relaxed">
              While we strive to provide accurate and current information, Redspire makes no representations or warranties regarding the accuracy, completeness, or reliability of content on this website. Information may become outdated, and technical standards, codes, and regulations change over time.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Project descriptions and case studies represent work performed under specific conditions and constraints. Results, timelines, and costs will vary for different projects.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">4. Project-Specific Limitations</h2>
            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">4.1 Site-Specific Conditions</h3>
            <p className="text-gray-700 leading-relaxed">
              Every project site has unique characteristics including soil conditions, topography, hydrology, environmental constraints, and regulatory requirements. Engineering solutions that work for one site may not be appropriate for another. Site-specific investigation and analysis are essential for proper engineering design.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">4.2 Regulatory Compliance</h3>
            <p className="text-gray-700 leading-relaxed">
              Regulatory requirements vary by jurisdiction and change over time. Information about permits, approvals, and regulatory processes is general in nature. Consult with local authorities and qualified professionals regarding specific regulatory requirements for your project.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">4.3 Cost Estimates</h3>
            <p className="text-gray-700 leading-relaxed">
              Any cost information or project budgets mentioned are approximate and based on specific project conditions at the time of work. Actual costs depend on market conditions, site-specific requirements, scope changes, and numerous other factors.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">5. Third-Party Content</h2>
            <p className="text-gray-700 leading-relaxed">
              This website may contain links to external websites, reference third-party products or services, or display content from third parties. Redspire does not endorse, control, or assume responsibility for third-party content, products, services, or websites. Access and use of third-party resources is at your own risk.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Reference to third-party standards, specifications, or organizations does not imply endorsement or guarantee of compliance.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">6. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To the maximum extent permitted by law, Redspire and its officers, employees, and affiliates shall not be liable for any damages arising from:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Use of or inability to use this website</li>
              <li>Reliance on website content without professional consultation</li>
              <li>Errors, omissions, or inaccuracies in website content</li>
              <li>Unauthorized access to or use of our servers or any personal information stored therein</li>
              <li>Interruption or cessation of website availability</li>
              <li>Third-party content, products, services, or websites</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              This limitation includes direct, indirect, incidental, consequential, special, punitive, or exemplary damages, even if Redspire has been advised of the possibility of such damages.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">7. Professional Licensing</h2>
            <p className="text-gray-700 leading-relaxed">
              Redspire Civil Engineering maintains professional engineering licenses as required by Connecticut and other applicable jurisdictions. However, professional engineering licenses are jurisdiction-specific. If your project is located in a jurisdiction where Redspire is not licensed, we may associate with or coordinate with locally-licensed professionals as appropriate.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Verify that engineering professionals working on your project hold appropriate licenses for your project location.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">8. Standard of Care</h2>
            <p className="text-gray-700 leading-relaxed">
              When engaged through a written agreement, Redspire provides services in accordance with the standard of care and diligence normally practiced by recognized professional engineering firms performing similar services under similar circumstances. This is not a warranty of perfection or guarantee of specific outcomes.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Engineering involves professional judgment based on available information, established principles, and applicable standards. Engineering cannot eliminate all risk or guarantee specific results.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">9. Construction Means and Methods</h2>
            <p className="text-gray-700 leading-relaxed">
              Unless specifically included in the scope of services, Redspire does not provide construction means, methods, techniques, sequences, or procedures. These are the responsibility of contractors. Redspire's review of contractor submittals is for general conformance with design intent only and does not relieve contractors of their responsibilities.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">10. Underground Conditions</h2>
            <p className="text-gray-700 leading-relaxed">
              Subsurface conditions can only be determined through investigation and testing. Even comprehensive geotechnical investigations provide limited information about a site. Actual conditions encountered during construction may differ from those indicated by investigation. Additional investigation or design modifications may be necessary when unexpected conditions are encountered.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">11. Environmental Conditions</h2>
            <p className="text-gray-700 leading-relaxed">
              Unless specifically included in scope, Redspire does not perform environmental site assessments or hazardous materials investigations. The presence of hazardous materials, contamination, or regulated substances requires specialized environmental investigation and may significantly affect project design, cost, and schedule.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">12. Use of Designs</h2>
            <p className="text-gray-700 leading-relaxed">
              Designs, drawings, specifications, and other deliverables are prepared for the specific project and site for which they were commissioned. Reuse for other projects or sites, or modifications without Redspire's involvement, is at the user's sole risk and releases Redspire from all liability associated with such use.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">13. Changes to This Disclaimer</h2>
            <p className="text-gray-700 leading-relaxed">
              Redspire reserves the right to modify this Legal Disclaimer at any time without prior notice. Changes will be effective immediately upon posting to the website. Continued use of the website after changes constitutes acceptance of the modified disclaimer.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">14. Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              This Legal Disclaimer and your use of this website are governed by the laws of the State of Connecticut, without regard to conflict of law provisions.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">15. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed">
              For questions regarding this Legal Disclaimer, contact:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg mt-4">
              <p className="text-gray-900 font-semibold mb-2">Redspire Civil Engineering</p>
              <p className="text-gray-700">218 Brookside Industrial Park</p>
              <p className="text-gray-700">Stamford, CT 06902</p>
              <p className="text-gray-700 mt-3">Email: <a href="mailto:info@redspire.com" className="text-red-600 hover:underline">info@redspire.com</a></p>
              <p className="text-gray-700">Phone: <a href="tel:+12034963375" className="text-red-600 hover:underline">+1 (203) 496-3375</a></p>
            </div>
          </section>

          <div className="border-t border-gray-200 pt-8 mt-12">
            <p className="text-sm text-gray-600">
              Last Updated: January 1, 2025
            </p>
            <p className="text-sm text-gray-600 mt-2">
              © 2025 Redspire Civil Engineering. All rights reserved.
            </p>
          </div>

        </div>
      </article>
    </Layout>
  );
};

export default LegalDisclaimerPage;
